import React, {FunctionComponent} from "react";
import {Box, Card, DialogActions, DialogContent, DialogTitle, Typography} from "@mui/material";
import {useTranslation} from "react-i18next";
import Dialog from "../../../../../../components/base/dialog";
import Button from "../../../../../../components/base/button/button";
import {PlanningDraftChange, PlanningDraftChangeTone} from "../planning-draft";

export interface PurchasePlanningChangesDialogProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    changes: PlanningDraftChange[];
}

const toneColor: Record<PlanningDraftChangeTone, string> = {
    primary: 'primary.main',
    secondary: 'secondary.main',
    tertiary: 'tertiary.main',
    disabled: 'text.disabled',
};

const PurchasePlanningChangesDialog: FunctionComponent<PurchasePlanningChangesDialogProps> = (props) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.purchase-planning-details'});
    const newestFirst = props.changes.slice().reverse();

    return (
        <Dialog
            open={props.open}
            setOpen={props.setOpen}
            maxWidth={'sm'}
            fullWidth
            className={'bound-dialog'}
        >
            <DialogTitle component={'div'}>
                <Typography variant={'h6'} sx={{p: 2}}>{t($ => $.changes)}</Typography>
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 1, p: 2}}>
                    {
                        !newestFirst.length &&
                        <Card
                            variant={'outlined'}
                            sx={{
                                boxShadow: 'none',
                                backgroundColor: 'background.paper',
                                borderColor: 'divider',
                                paddingBlock: 1.5,
                                paddingInline: 2,
                            }}
                        >
                            <Typography variant={'body2'} color={'text.disabled'}>{t($ => $.noChanges)}</Typography>
                        </Card>
                    }
                    {
                        newestFirst.map(change => {
                            const segments = change.segments?.length ? change.segments : [{text: change.label}];
                            return (
                                <Card
                                    key={change.id}
                                    variant={'outlined'}
                                    sx={{
                                        boxShadow: 'none',
                                        backgroundColor: 'background.paper',
                                        borderColor: 'divider',
                                        paddingBlock: 1,
                                        paddingInline: 1.5,
                                    }}
                                >
                                    <Typography variant={'body2'} color={'text.primary'}>
                                        {t($ => $.changeKind[change.kind])}
                                    </Typography>
                                    <Typography variant={'body2'} color={'text.primary'} component={'div'}>
                                        {segments.map((segment, index) => (
                                            <Box
                                                key={`${change.id}-${index}`}
                                                component={'span'}
                                                sx={{color: segment.tone ? toneColor[segment.tone] : 'text.primary'}}
                                            >
                                                {segment.text}
                                            </Box>
                                        ))}
                                    </Typography>
                                </Card>
                            );
                        })
                    }
                </Box>
            </DialogContent>
            <DialogActions sx={{p: 0, m: 0, display: 'flex', justifyContent: 'space-between'}}>
                <Button
                    variant={'dialog'}
                    size={'medium'}
                    type={'button'}
                    color={'primary'}
                    borderRadius={'0 8px 0 8px'}
                    onClick={() => props.setOpen(false)}
                >
                    {t($ => $.close)}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PurchasePlanningChangesDialog;
